import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { PROPERTY_KEYS, RECOVERY_DEFAULTS } from '../../config';
import {
  recordRecoveryAttempt,
  selectPropertiesApp,
  selectRecoveryAttempt,
  useConfirmUsernameMutation,
  useRestorePasswordMutation,
} from '../../redux';
import { getRecoveryBlock } from '../../utils/recoveryBlock';
import { resolveRecoveryError } from '../../utils/recoveryError';
import { toHttps } from '../../utils/serviceUrl';

export interface RecoverPasswordFormFields {
  identificationValue: string;
  phone: string;
}

const keyPrefix = 'hooks.formManagements.useRecoverPasswordFormManagement';

const defaultValues: RecoverPasswordFormFields = {
  identificationValue: '',
  phone: '',
};

/**
 * Lo que valida el `RestorePasswordDTO` del backend: validarlo antes evita
 * gastar un intento en un error de formato. El formulario toma de aquí sus
 * `maxLength`.
 */
export const RECOVERY_FIELD_LIMITS = {
  identificationMin: 4,
  identificationMax: 40,
  phoneMin: 7,
  phoneMax: 10,
} as const;

const { identificationMin, identificationMax, phoneMin, phoneMax } =
  RECOVERY_FIELD_LIMITS;
const PHONE_PATTERN = new RegExp(`^\\d{${phoneMin},${phoneMax}}$`);

/** Un número publicado en el catálogo, o el valor por defecto si falta o no es válido. */
const positiveIntOr = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Recuperación de contraseña contra NestJS, en dos pasos, igual que la app
 * (VWA-37): el vendedor da documento y línea, ve el usuario que les corresponde
 * y lo confirma. Solo entonces el backend genera la temporal y la envía.
 */
export const useRecoverPasswordFormManagement = () => {
  const [genericErr, setGenericErr] = useState<string | null>(null);
  const [response, setResponse] = useState<string | null>(null);
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const { t } = useTranslation('translation', { keyPrefix });
  const dispatch = useDispatch();

  const properties = useSelector(selectPropertiesApp);
  const attempt = useSelector(selectRecoveryAttempt);

  const restorePasswordUrl = properties[PROPERTY_KEYS.RESTORE_PASSWORD];
  const confirmUsernameUrl = properties[PROPERTY_KEYS.CONFIRM_USERNAME];
  const maxAttempts = positiveIntOr(
    properties[PROPERTY_KEYS.RECOVERY_MAX_ATTEMPTS],
    RECOVERY_DEFAULTS.maxAttempts
  );
  const lockoutSeconds = positiveIntOr(
    properties[PROPERTY_KEYS.RECOVERY_LOCKOUT_SECONDS],
    RECOVERY_DEFAULTS.lockoutSeconds
  );
  const retryIntervalSeconds = positiveIntOr(
    properties[PROPERTY_KEYS.RECOVERY_RETRY_INTERVAL_SECONDS],
    RECOVERY_DEFAULTS.retryIntervalSeconds
  );

  const block = getRecoveryBlock({
    attempt,
    now,
    lockoutSeconds,
    retryIntervalSeconds,
  });

  const [restorePassword, { isLoading: isRestoring }] =
    useRestorePasswordMutation();
  const [confirmUsername, { isLoading: isConfirming }] =
    useConfirmUsernameMutation();

  /**
   * El `.trim()` del schema es lo que recorta los extremos de lo que se envía:
   * `yupResolver` entrega al submit los valores ya transformados. Un espacio
   * pegado no debe costar un intento (hallazgo F).
   */
  const schema = yup.object().shape({
    identificationValue: yup
      .string()
      .trim()
      .required(t('schema.identificationValue.required'))
      .min(identificationMin, t('schema.identificationValue.min'))
      .max(identificationMax, t('schema.identificationValue.max')),
    phone: yup
      .string()
      .trim()
      .required(t('schema.phone.required'))
      .matches(PHONE_PATTERN, t('schema.phone.format')),
  });

  const methods = useForm<RecoverPasswordFormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  /**
   * La cuenta regresiva solo avanza mientras hay un bloqueo: fuera de él no
   * hace falta volver a pintar cada segundo.
   */
  const blocked = block !== null;
  useEffect(() => {
    if (!blocked) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [blocked]);

  const clearErrorMessages = (): void => {
    setGenericErr(null);
    setResponse(null);
  };

  const submit: SubmitHandler<RecoverPasswordFormFields> = async ({
    identificationValue,
    phone,
  }) => {
    clearErrorMessages();

    const currentBlock = getRecoveryBlock({
      attempt,
      now: Date.now(),
      lockoutSeconds,
      retryIntervalSeconds,
    });
    if (currentBlock) {
      setNow(Date.now());
      return;
    }

    if (!restorePasswordUrl) {
      setGenericErr(t('errors.connection'));
      return;
    }

    try {
      const { username } = await restorePassword({
        identificationValue,
        phone,
        url: toHttps(restorePasswordUrl),
      }).unwrap();
      setPendingUsername(username);
    } catch (err) {
      setGenericErr(resolveRecoveryError(err, t));
    } finally {
      const at = Date.now();
      dispatch(recordRecoveryAttempt({ at, maxAttempts }));
      setNow(at);
    }
  };

  /** El vendedor reconoce el usuario: ahora sí se genera y envía la temporal. */
  const confirm = async (): Promise<void> => {
    const username = pendingUsername;
    setPendingUsername(null);
    clearErrorMessages();

    if (!username) return;
    if (!confirmUsernameUrl) {
      setGenericErr(t('errors.connection'));
      return;
    }

    try {
      const message = await confirmUsername({
        username,
        url: toHttps(confirmUsernameUrl),
      }).unwrap();
      setResponse(message?.trim() || t('success.default'));
    } catch (err) {
      setGenericErr(resolveRecoveryError(err, t));
    }
  };

  /** No es su usuario: vuelve al formulario sin que cambie ninguna contraseña. */
  const reject = (): void => setPendingUsername(null);

  return {
    block,
    genericErr,
    loading: isRestoring || isConfirming,
    methods,
    pendingUsername,
    response,
    clearErrorMessages,
    confirm,
    reject,
    submit,
  };
};
